import Module from '../module';
import scroll from '../../libs/scroll/scroll';
import formValidatorFactory, {
	RequiredFieldObject,
	EmailFieldObject,
	ValidatorFieldObject
} from '../../libs/form/form-validator';
import jumpFactory from '../../libs/jump/jump';
import alertServiceFactory from '../../libs/alert/alert-service';

interface OptionsInterface {
	cssId: {
		headerCls?: string;
		anim?: {
			contactFormContentSectionHeaderBigV2Cls: string;
			contactFormCustomFormWrapperCls: string;
		}

		form?: {
			contactFormInputFirstNameId: string;
			contactFormInputLastNameId: string;
			contactFormInputEmailAddressId: string;
			contactFormInputPhoneId: string;
			contactFormInputStateProvinceId: string;
			contactFormSelectCountryId: string;
			contactFormInputCompanyNameId: string;
			contactFormTextareaCommentId: string;
			contactFormInputRadioInvestorId: string;
			contactFormInputRadioNotInvestorId: string;
			contactFormInputErrorCls: string;
			contactFormSelectErrorCls: string;
			contactFormTextareaErrorCls: string;
			contactFormInputRadioErrorCls: string;
			contactFormFormId: string;
			contactFormCustomFormWrapperCls: string;
			customFormWrapperLoadingCls: string;
			customFormWrapperThxCls: string;
			contactFormCustomFormThxBtnCls: string;
			contactFormCustomFormLoaderWrapperCls: string;
			customFormHeaderThxSuccessCls: string;
			customFormHeaderThxFailedCls: string;
			customFormHeaderThxDisabledCls: string;
		}
	},

	rand?: string,
	recaptchaSiteKey?: string,
	slug: string,
	contactMsgEndpoint?: string,
	contactMsgEndpointAction?: string
}

interface ServiceInterface {
	formValidator?: ReturnType<typeof formValidatorFactory.create>;
	jump?: ReturnType<typeof jumpFactory.create>;
	alertService?: ReturnType<typeof alertServiceFactory.create>;
}

declare global {
	interface Window {
		grecaptcha: {
			enterprise: {
				ready: (callback: () => void) => void,
				execute: (recaptchaSiteKey: string, options: {
					action: string
				}) => string
			}
		};
	}
}
class Form extends Module {

	publicVars: OptionsInterface;
	service: ServiceInterface = {};

	privateVars: {
		formInitialSend: boolean,
		requiredFields: Record<string, Omit<RequiredFieldObject, 'status'>>
		emailFields: Record<string, Omit<EmailFieldObject, 'status'>>,
		validatorFields: Record<string, Omit<ValidatorFieldObject, 'status'>>
	} = {
		formInitialSend: false,
		requiredFields: {},
		emailFields: {},
		validatorFields: {}
	};

	constructor(options: OptionsInterface) {
		super('Contact Form Module');

		this.publicVars = options;
	}

	startup() {
		this.msg('startup');

		scroll.init();
		scroll.registerAnimatedElement(this.publicVars.cssId.anim?.contactFormContentSectionHeaderBigV2Cls || '', 'contact_form', { animCls: 'animate__zoomIn50P', once: true});
		scroll.registerAnimatedElement(this.publicVars.cssId.anim?.contactFormCustomFormWrapperCls || '', 'contact_form', { animCls: 'animate__fadeIn', once: true});

		this.service.jump = jumpFactory.create();
		this.service.jump.init();

		this.service.formValidator = formValidatorFactory.create();
		this.service.formValidator.init();

		if (this.publicVars.cssId.form?.contactFormInputFirstNameId) {
			this.privateVars.requiredFields['firstName'] = {
				field: this.publicVars.cssId.form.contactFormInputFirstNameId,
				errorField: this.publicVars.cssId.form.contactFormInputFirstNameId,
				errorClass: this.publicVars.cssId.form.contactFormInputErrorCls.substring(1),
				defaultValue: ''
			};
		}

		if (this.publicVars.cssId.form?.contactFormInputLastNameId) {
			this.privateVars.requiredFields['lastName'] = {
				field: this.publicVars.cssId.form.contactFormInputLastNameId,
				errorField: this.publicVars.cssId.form.contactFormInputLastNameId,
				errorClass: this.publicVars.cssId.form.contactFormInputErrorCls.substring(1),
				defaultValue: ''
			};
		}

		if (this.publicVars.cssId.form?.contactFormInputStateProvinceId) {
			this.privateVars.requiredFields['stateProvince'] = {
				field: this.publicVars.cssId.form.contactFormInputStateProvinceId,
				errorField: this.publicVars.cssId.form.contactFormInputStateProvinceId,
				errorClass: this.publicVars.cssId.form.contactFormInputErrorCls.substring(1),
				defaultValue: ''
			};
		}

		if (this.publicVars.cssId.form?.contactFormSelectCountryId) {
			this.privateVars.requiredFields['country'] = {
				field: this.publicVars.cssId.form.contactFormSelectCountryId,
				errorField: this.publicVars.cssId.form.contactFormSelectCountryId,
				errorClass: this.publicVars.cssId.form.contactFormSelectErrorCls.substring(1),
				defaultValue: ''
			};
		}

		if (this.publicVars.cssId.form?.contactFormTextareaCommentId) {
			this.privateVars.requiredFields['comment'] = {
				field: this.publicVars.cssId.form.contactFormTextareaCommentId,
				errorField: this.publicVars.cssId.form.contactFormTextareaCommentId,
				errorClass: this.publicVars.cssId.form.contactFormTextareaErrorCls.substring(1),
				defaultValue: ''
			};
		}

		if (this.publicVars.cssId.form?.contactFormInputEmailAddressId) {
			this.privateVars.emailFields = {
				'email': {
					field: this.publicVars.cssId.form.contactFormInputEmailAddressId,
					errorField: this.publicVars.cssId.form.contactFormInputEmailAddressId,
					errorClass: this.publicVars.cssId.form.contactFormInputErrorCls.substring(1),
					isRequired: true,
					defaultValue: ''
				}
			};
		}

		if (this.publicVars.cssId.form?.contactFormInputRadioInvestorId &&
			this.publicVars.cssId.form?.contactFormInputRadioNotInvestorId) {
			this.privateVars.validatorFields = {
				'code': {
					field: this.publicVars.cssId.form.contactFormInputRadioInvestorId,
					errorField: this.publicVars.cssId.form.contactFormInputRadioInvestorId,
					errorClass: this.publicVars.cssId.form.contactFormInputRadioErrorCls.substring(1),
					defaultValue: '',
					validator: (value: string) => {

						const radioInvestorHtml = document.querySelector(
							this.publicVars.cssId.form?.contactFormInputRadioInvestorId!) as HTMLInputElement;
						const radioNotInvestorHtml = document.querySelector(
							this.publicVars.cssId.form?.contactFormInputRadioNotInvestorId!) as HTMLInputElement;

						const isSelected = (radioInvestorHtml.checked || radioNotInvestorHtml.checked);

						if (isSelected) {
							radioNotInvestorHtml.classList.remove(
								this.publicVars.cssId.form?.contactFormInputRadioErrorCls.substring(1) || '');
							radioInvestorHtml.classList.remove(
								this.publicVars.cssId.form?.contactFormInputRadioErrorCls.substring(1) || '');
						} else {
							radioNotInvestorHtml.classList.add(
								this.publicVars.cssId.form?.contactFormInputRadioErrorCls.substring(1) || '');
							radioInvestorHtml.classList.add(
								this.publicVars.cssId.form?.contactFormInputRadioErrorCls.substring(1) || '');
						}

						return isSelected;
					}
				},
			};
		}

		this.service.formValidator.setRequiredFields(this.privateVars.requiredFields);
		this.service.formValidator.setEmailFields(this.privateVars.emailFields);
		this.service.formValidator.setValidatorFields(this.privateVars.validatorFields);
	}

	bind() {
		this.msg('bind');

		this.bindFormElements();
	}

	bindFormElements() {
		if (this.publicVars.cssId.form?.contactFormFormId) {
			const formHtml = document.querySelector(this.publicVars.cssId.form.contactFormFormId) as HTMLFormElement;

			formHtml?.addEventListener('submit', (e) => {
				this.handleFormSubmit(e);
			});
		}

		if (this.publicVars.cssId.form?.contactFormInputFirstNameId) {
			const firstNameInputHtml = document.querySelector(this.publicVars.cssId.form.contactFormInputFirstNameId);

			firstNameInputHtml?.addEventListener('blur', () => {
				if (this.privateVars.formInitialSend) {
					this.service.formValidator?.validateRequiredField('firstName', true);
				}
			});

			firstNameInputHtml?.addEventListener('focus', () => {
				firstNameInputHtml.classList.remove(
					this.publicVars.cssId.form?.contactFormInputErrorCls.substring(1) || '');
			});
		}

		if (this.publicVars.cssId.form?.contactFormInputLastNameId) {
			const lastNameInputHtml = document.querySelector(this.publicVars.cssId.form.contactFormInputLastNameId);

			lastNameInputHtml?.addEventListener('blur', () => {
				if (this.privateVars.formInitialSend) {
					this.service.formValidator?.validateRequiredField('lastName', true);
				}
			});

			lastNameInputHtml?.addEventListener('focus', () => {
				lastNameInputHtml.classList.remove(
					this.publicVars.cssId.form?.contactFormInputErrorCls.substring(1) || '');
			});
		}

		if (this.publicVars.cssId.form?.contactFormInputStateProvinceId) {
			const stateProvinceInputHtml = document.querySelector(
				this.publicVars.cssId.form.contactFormInputStateProvinceId);

			stateProvinceInputHtml?.addEventListener('blur', () => {
				if (this.privateVars.formInitialSend) {
					this.service.formValidator?.validateRequiredField('stateProvince', true);
				}
			});

			stateProvinceInputHtml?.addEventListener('focus', () => {
				stateProvinceInputHtml.classList.remove(
					this.publicVars.cssId.form?.contactFormInputErrorCls.substring(1) || '');
			});
		}

		if (this.publicVars.cssId.form?.contactFormTextareaCommentId) {
			const commentTextareaHtml = document.querySelector(this.publicVars.cssId.form.contactFormTextareaCommentId);

			commentTextareaHtml?.addEventListener('blur', () => {
				if (this.privateVars.formInitialSend) {
					this.service.formValidator?.validateRequiredField('comment', true);
				}
			});

			commentTextareaHtml?.addEventListener('focus', () => {
				commentTextareaHtml.classList.remove(
					this.publicVars.cssId.form?.contactFormTextareaErrorCls.substring(1) || '');
			});
		}


		if (this.publicVars.cssId.form?.contactFormSelectCountryId) {
			const countrySelectHtml = document.querySelector(this.publicVars.cssId.form.contactFormSelectCountryId);

			countrySelectHtml?.addEventListener('blur', () => {
				if (this.privateVars.formInitialSend) {
					this.service.formValidator?.validateRequiredField('country', true);
				}
			});

			countrySelectHtml?.addEventListener('focus', () => {
				countrySelectHtml.classList.remove(
					this.publicVars.cssId.form?.contactFormSelectErrorCls.substring(1) || '');
			});
		}

		if (this.publicVars.cssId.form?.contactFormInputEmailAddressId) {
			const emailAddressInputHtml = document.querySelector(
				this.publicVars.cssId.form.contactFormInputEmailAddressId);

			emailAddressInputHtml?.addEventListener('blur', () => {
				if (this.privateVars.formInitialSend) {
					this.service.formValidator?.validateEmailField('email', true);
				}
			});

			emailAddressInputHtml?.addEventListener('focus', () => {
				emailAddressInputHtml.classList.remove(
					this.publicVars.cssId.form?.contactFormInputErrorCls.substring(1) || '');
			});
		}
		if (this.publicVars.cssId.form?.contactFormInputRadioInvestorId &&
			this.publicVars.cssId.form?.contactFormInputRadioNotInvestorId) {
			const radioInvestorHtml = document.querySelector(
				this.publicVars.cssId.form?.contactFormInputRadioInvestorId) as HTMLInputElement;
			const radioNotInvestorHtml = document.querySelector(
				this.publicVars.cssId.form?.contactFormInputRadioNotInvestorId) as HTMLInputElement;

			radioInvestorHtml?.addEventListener('click', () => {
				radioInvestorHtml.classList.remove(
					this.publicVars.cssId.form?.contactFormInputRadioErrorCls.substring(1) || '');
				radioNotInvestorHtml.classList.remove(
					this.publicVars.cssId.form?.contactFormInputRadioErrorCls.substring(1) || '');
			});

			radioNotInvestorHtml?.addEventListener('click', () => {
				radioInvestorHtml.classList.remove(
					this.publicVars.cssId.form?.contactFormInputRadioErrorCls.substring(1) || '');
				radioNotInvestorHtml.classList.remove(
					this.publicVars.cssId.form?.contactFormInputRadioErrorCls.substring(1) || '');
			});
		}

		if(this.publicVars.cssId.form?.contactFormCustomFormThxBtnCls) {
			const thxBtnCloseHtml = document.querySelector(this.publicVars.cssId.form?.contactFormCustomFormThxBtnCls);

			thxBtnCloseHtml?.addEventListener('click', () => {
				this.closeThxFormMsg();
			})
		}
	}

	async handleFormSubmit(e: SubmitEvent) {
		e.preventDefault();

		this.privateVars.formInitialSend = true;

		if (!this.service.formValidator) {
			this.errorMsg('formValidator service not found');
			return;
		}

		if (!this.publicVars.cssId.form?.contactFormFormId) {
			this.errorMsg('form id not found');
			return;
		}

		if (!this.publicVars.recaptchaSiteKey) {
			this.errorMsg('recaptcha key is not found');
			return;
		}


		const formHtml = document.querySelector(this.publicVars.cssId.form.contactFormFormId) as HTMLFormElement;

		if (!formHtml || !this.publicVars?.contactMsgEndpoint || !this.publicVars?.contactMsgEndpointAction || !this.publicVars.rand) {
			this.errorMsg('form config error');
			return;
		}

		const validationResults = this.service.formValidator.validate();

		if (!validationResults.validFlag) {

			let modifier = this.getHeaderHeight() + 20;

			if (this.service.jump) {
				this.service.jump.centerTo(this.publicVars.cssId.form.contactFormInputErrorCls, {
					speed: 200,
					modifier: modifier
				}, () => {
				});
			}
		} else {
			this.msg('form validation passed');

			const formWrapperHtml = document.querySelector(
				this.publicVars.cssId.form.contactFormCustomFormWrapperCls);

			formWrapperHtml?.classList.add(this.publicVars.cssId.form?.customFormWrapperLoadingCls.substring(1));

			let modifier = this.getHeaderHeight() + 20;

			if (this.service.jump) {
				this.service.jump.centerTo(this.publicVars.cssId.form.contactFormCustomFormLoaderWrapperCls, {
					speed: 200,
					modifier: modifier
				}, () => {
				});
			}

			const recaptchaSiteKey = this.publicVars.recaptchaSiteKey

			const that = this;

			setTimeout(function () {
				window.grecaptcha.enterprise.ready(async function() {

					const token = await window.grecaptcha.enterprise.execute(recaptchaSiteKey, {action: 'submit'});

					that.executeFormSend(token);
				});

			}, 300);
		}
	}

	async executeFormSend(token: string) {
		if (!this.publicVars.cssId.form?.contactFormFormId) {
			this.errorMsg('form id not found');
			return;
		}

		const formHtml = document.querySelector(this.publicVars.cssId.form.contactFormFormId) as HTMLFormElement;

		if (!formHtml || !this.publicVars?.contactMsgEndpoint || !this.publicVars?.contactMsgEndpointAction || !this.publicVars.rand) {
			this.errorMsg('form config error');
			return;
		}

		const formWrapperHtml = document.querySelector(
			this.publicVars.cssId.form.contactFormCustomFormWrapperCls);

		const firstNameHtml = document.querySelector(this.publicVars.cssId.form.contactFormInputFirstNameId) as HTMLInputElement;
		const lastNameHtml = document.querySelector(this.publicVars.cssId.form.contactFormInputLastNameId) as HTMLInputElement;
		const emailHtml = document.querySelector(this.publicVars.cssId.form.contactFormInputEmailAddressId) as HTMLInputElement;
		const phoneHtml = document.querySelector(this.publicVars.cssId.form.contactFormInputPhoneId) as HTMLInputElement;
		const companyNameHtml = document.querySelector(this.publicVars.cssId.form.contactFormInputCompanyNameId) as HTMLInputElement;
		const stateProvinceHtml = document.querySelector(this.publicVars.cssId.form.contactFormInputStateProvinceId) as HTMLInputElement;
		const countryHtml = document.querySelector(this.publicVars.cssId.form.contactFormSelectCountryId) as HTMLSelectElement;
		const commentHtml = document.querySelector(this.publicVars.cssId.form.contactFormTextareaCommentId) as HTMLTextAreaElement;
		const investorHtml = document.querySelector(this.publicVars.cssId.form.contactFormInputRadioInvestorId) as HTMLInputElement;

		let formData = new FormData();

		formData.append('rand', this.publicVars.rand);
		formData.append('act', this.publicVars.contactMsgEndpointAction);
		formData.append('firstName', firstNameHtml?.value);
		formData.append('lastName', lastNameHtml?.value);
		formData.append('email', emailHtml?.value);
		formData.append('phone', phoneHtml?.value);
		formData.append('companyName', companyNameHtml?.value);
		formData.append('stateProvince', stateProvinceHtml?.value);
		formData.append('country', countryHtml?.value);
		formData.append('comment', commentHtml?.value);
		formData.append('investor', investorHtml?.checked ? '1' : '0');
		formData.append('recaptchaToken', token);

		const response = await fetch(this.publicVars.contactMsgEndpoint, {
			method: 'POST',
			body: formData
		});

		const customFormHeaderThxSuccessHtml = document.querySelector(this.publicVars.cssId.form?.customFormHeaderThxSuccessCls) as HTMLElement;
		const customFormHeaderThxFailedHtml = document.querySelector(this.publicVars.cssId.form?.customFormHeaderThxFailedCls) as HTMLElement;

		if(!response.ok) {
			formWrapperHtml?.classList.remove(this.publicVars.cssId.form?.customFormWrapperLoadingCls.substring(1) || '');
			customFormHeaderThxSuccessHtml.classList.add(this.publicVars.cssId.form.customFormHeaderThxDisabledCls.substring(1) || '');
			customFormHeaderThxFailedHtml.classList.remove(this.publicVars.cssId.form.customFormHeaderThxDisabledCls.substring(1) || '');
			formWrapperHtml?.classList.add(this.publicVars.cssId.form?.customFormWrapperThxCls.substring(1) || '');
			return;
		}

		const data = await response.json();

		setTimeout(() => {
			customFormHeaderThxSuccessHtml.classList.remove(this.publicVars.cssId.form?.customFormHeaderThxDisabledCls.substring(1) || '');
			customFormHeaderThxFailedHtml.classList.add(this.publicVars.cssId.form?.customFormHeaderThxDisabledCls.substring(1) || '');
			formWrapperHtml?.classList.remove(this.publicVars.cssId.form?.customFormWrapperLoadingCls.substring(1) || '');
			formWrapperHtml?.classList.add(this.publicVars.cssId.form?.customFormWrapperThxCls.substring(1) || '');
		}, 1000);
	}

	closeThxFormMsg() {
		if(!this.publicVars.cssId.form?.contactFormCustomFormWrapperCls) {
			this.errorMsg('form config error');
			return
		}

		const formWrapperHtml = document.querySelector(
				this.publicVars.cssId.form?.contactFormCustomFormWrapperCls);

		formWrapperHtml?.classList.remove(this.publicVars.cssId.form?.customFormWrapperThxCls.substring(1) || '');

		const formHtml = document.querySelector(this.publicVars.cssId.form.contactFormFormId) as HTMLFormElement;

		if (!formHtml) {
			this.errorMsg('form not found');
			return;
		}

		formHtml.reset();
	}

	getHeaderHeight() {
		let headerHeight = 0;

		if (this.publicVars.cssId.headerCls) {
			const header = document.querySelector(this.publicVars.cssId.headerCls) as HTMLElement;

			headerHeight = header?.offsetHeight || 0;
		}

		return headerHeight;
	}
}

export default Form;